import * as auth from "./auth";
import * as event from "./event";
import * as router from "../router";
import * as oss from "../service/oss";
import { getSystemReleasePath } from "./system";
import { getOptions, getRouter } from "./options";
import { user, system, position, resource } from "../redux/reducers";

/**
 * 初始化应用
 * @param {object} options 参数
 */
export function initApp(options = {}) {
  return new Promise((resolve, reject) => {
    // 初始化系统发布
    initSystemRelease().then(() => {
      // 检查登录状态
      checkLogin().then(res => {
        const { login } = res;
        if (login) {
          // 初始化登录
          initLogin().then(() => {
            // 如果当前是登录页，则跳转到登录后页面
            const { loginPath, loginedPath, } = getRouter();
            const releaseLoginPath = getSystemReleasePath(loginPath);
            if (releaseLoginPath === window.location.pathname) {
              router.history.push(loginedPath);
            }
            resolve();
          });
          return;
        }
        const { ignores } = getRouter();
        if (ignores) {
          let { pathname } = window.location;
          let releasePath = getSystemReleasePath("");
          if (pathname.startsWith(releasePath)) {
            pathname = pathname.substring(releasePath.length);
          }
          if (!ignores.includes(pathname)) {
            router.history.push(getRouter().loginPath);
          }
        }
        resolve();
      });
    }).catch(res => {
      reject(res);
    });
  });
}

/**
 * 检查登录
 * @returns
 */
function checkLogin() {
  return new Promise((resolve, reject) => {
    const token = auth.getToken();
    if (token) {
      user.actions.checkLogin({ token }).then(res => {
        const { login } = res.data;
        if (false === login) {
          auth.reset();
        }
        resolve({ login, });
      }).catch(() => {
        auth.reset();
        resolve({ login: false, });
      });
      return;
    }
    auth.reset();
    resolve({ login: false, });
  });
}

/**
 * 初始化系统发布
 * @returns
 */
function initSystemRelease() {
  return new Promise((resolve, reject) => {
    system.actions.getAllSystemRelease().then(res => {
      // 去除应用上下文
      const releases = res.data || [];
      const { context } = getOptions();
      let pathname = window.location.pathname;
      if (pathname.startsWith(`${context}/`)) {
        pathname = pathname.replace(`${context}/`, "");
      }
      // 匹配系统发布上下文
      let release = null;
      const pathContext = pathname.split("/")[0];
      for (let i = 0; i < releases.length; ++i) {
        const item = releases[i];
        if (pathContext === item.context) {
          release = item;
          break;
        }
      }
      if (release) {
        // 初始化系统发布
        initSystemReleaseStyle(release);
        system.actions.initSystemRelease(release);
        resolve();
      } else {
        reject({ code: -1, message: "未匹配系统发布上下文" });
      }
    }).catch(res => reject(res));
  });
}

/**
 * 初始化系统发布样式
 * @returns
 */
function initSystemReleaseStyle(release) {
  if (release.title) {
    // 标题
    document.title = release.title;
  }
  if (release.favicon) {
    // Favicon 图标
    var link = document.querySelector("link[rel*='icon']") || document.createElement("link");
    link.type = "image/x-icon";
    link.rel = "shortcut icon";
    link.href = oss.getUrl(release.favicon);
    document.getElementsByTagName("head")[0].appendChild(link);
  }
}

/**
 * 初始化登录
 * @returns
 */
function initLogin() {
  return new Promise((resolve, reject) => {
    // 初始化用户信息
    initUserInfo();
    // 初始化岗位信息
    initPosition().then(() => {
      resolve();
      // 发射登录成功事件
      event.login.emit();
    }).catch(res => {
      reject(res);
    });
  });
}

/**
 * 初始化用户信息
 * @returns
 */
function initUserInfo() {
  user.actions.getUserInfo();
}

/**
 * 初始化岗位信息
 * @returns
 */
function initPosition() {
  return new Promise((resolve, reject) => {
    return initPositionList().then((res) => {
      const positionList = res.data;
      if (!positionList.length) {
        // TODO 无岗位
        reject({ code: -1, message: "无岗位信息" });
        return;
      }
      // 选取岗位
      let positionId = auth.getPosition();
      if (positionId) {
        // 判断是否存在该岗位
        const position = positionList.find(item => positionId === item.id);
        if (!position) {
          // 重新选取
          positionId = positionList[0].id;
        }
      } else {
        positionId = positionList[0].id;
      }
      // 切换岗位
      switchPosition({ positionId, }).then(() => {
        resolve();
      }).catch(res => {
        reject(res);
      });
    }).catch(res => {
      reject(res);
    });
  });
}
/**
 * 初始化岗位列表
 * @returns
 */
function initPositionList() {
  return new Promise((resolve, reject) => {
    const { release } = system.state;
    const paramPosition = { systemId: release.systemId, };
    position.actions.getPositionList(paramPosition).then((res) => {
      resolve(res);
    }).catch(res => {
      reject(res);
    });
  });
}

/**
 * 切换岗位
 * @returns
 */
export function switchPosition(param) {
  return new Promise((resolve, reject) => {
    const { positionId } = param;
    const paramSwitch = { positionId, };
    position.actions.switchPosition(paramSwitch).then((res) => {
      // 保存岗位ID
      const { position, } = res.data;
      auth.setPosition(position.id);
      // 获取菜单权限
      resource.actions.getMenuList({
        "positionId": position.id,
        "systemId": system.state.release.systemId,
      });
      // 发送切换岗位事件
      event.switchPosition.emit();
      resolve(res);
    }).catch((res) => {
      reject(res);
    });
  });
}

/**
 * 登录
 * @param {object} options 参数
 */
export function login(options = {}) {
  return new Promise((resolve, reject) => {
    doLogin(options).then(res => {
      initLogin().then(res => {
        resolve(res);
      }).catch(res => {
        reject(res);
      });
    }).catch(res => {
      reject(res);
    });
  });
}

/**
 * 进行登录
 * @param {object} options 参数
 */
function doLogin(options = {}) {
  return new Promise((resolve, reject) => {
    let releaseId = options.releaseId;
    if (!releaseId) {
      const { release } = system.state;
      if (!release || !release.id) {
        reject({ code: -1, message: "获取发布系统信息错误" });
        return;
      }
      releaseId = release.id;
    }
    const loginInfo = {
      ...options,
      releaseId,
      username: options.username,
      password: options.password,
    };
    user.actions.login(loginInfo).then(res => {
      // 存储 Token
      const { tokenHead, token } = res.data;
      auth.setToken(`${tokenHead}${token}`);
      resolve(res);
    }).catch(res => {
      reject(res);
    });
  });
}

/**
 * 登出
 * @param {object} options 参数
 */
export function logout(options = {}) {
  return user.actions.logout().finally(() => {
    auth.reset();
    event.logout.emit();
  });
}
